import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import api, { assetUrl } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, Bar, BarChart, CartesianGrid, PieChart, Pie, Cell } from 'recharts';
import { toast } from 'sonner';
import { DollarSign, ShoppingBag, Users, Palette, RefreshCw, Sparkles, Upload, FileText, Mail, Trash2, Archive, ArchiveRestore, LayoutDashboard, Settings as SettingsIcon, Bell, TrendingUp, UserCog, Search, Shield, Receipt, FileDown, Edit3, Plus, Package, Send, Reply, X, Flag, ShieldCheck, ShieldAlert, ShieldX, Crown, Award, Gift, Minus, Power, PowerOff } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import RichEditor from '@/components/RichEditor';
import { useCurrency } from '@/context/CurrencyContext';
import { useConfirm } from '@/context/ConfirmContext';
import Pagination from '@/components/Pagination';
import BulkActionBar from '@/components/BulkActionBar';
import { useSelection } from '@/lib/useSelection';
import { openInvoice } from '@/lib/invoice';
import { Kpi } from './_shared';

const REPORT_REASON_LABEL = {
  copyright: 'Copyright / IP',
  nudity: 'Nudity / sexual',
  hate: 'Hate speech',
  violence: 'Violence',
  personal_info: 'Personal info',
  spam: 'Spam',
  other: 'Other',
};

export const ReportsSection = () => {
  const [tab, setTab] = useState('open');
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState(null);
  const [action, setAction] = useState('dismiss');
  const [notes, setNotes] = useState('');

  const load = () => {
    setLoading(true);
    api.get('/admin/reports', { params: { status: tab } })
      .then(({ data }) => setReports(data))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const openReview = (r, a) => { setTarget(r); setAction(a); setNotes(''); };

  const submitReview = async () => {
    if (!target) return;
    try {
      await api.patch(`/admin/reports/${target.id}`, { action, notes });
      toast.success(action === 'dismiss' ? 'Report dismissed' : 'Content removed');
      setTarget(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-semibold text-lg">Content Reports</h3>
          <p className="text-sm" style={{ color: 'var(--jw-text-muted)' }}>Flags submitted by users for review.</p>
        </div>
        <div className="-mx-4 sm:mx-0 overflow-x-auto jw-scrollbar">
          <div className="px-4 sm:px-0 min-w-max">
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList>
                <TabsTrigger value="open" data-testid="admin-reports-tab-open">Open</TabsTrigger>
                <TabsTrigger value="resolved" data-testid="admin-reports-tab-resolved">Resolved</TabsTrigger>
                <TabsTrigger value="dismissed" data-testid="admin-reports-tab-dismissed">Dismissed</TabsTrigger>
                <TabsTrigger value="all" data-testid="admin-reports-tab-all">All</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="jw-card p-10 text-center" style={{ color: 'var(--jw-text-muted)' }}>Loading…</div>
      ) : reports.length === 0 ? (
        <div className="jw-card p-10 text-center" style={{ color: 'var(--jw-text-muted)' }} data-testid="admin-reports-empty">
          <Flag className="w-8 h-8 mx-auto opacity-40" />
          <p className="mt-3">No reports here.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {reports.map((r) => (
            <Card key={r.id} className="jw-card p-4" data-testid={`admin-report-${r.id}`}>
              <div className="flex items-start gap-3">
                {r.entity?.preview_url ? (
                  <img src={assetUrl(r.entity.preview_url)} alt="" className="w-16 h-16 rounded-md object-cover flex-shrink-0" />
                ) : (
                  <div className="w-16 h-16 rounded-md bg-white/[0.05] grid place-items-center flex-shrink-0"><Flag className="w-5 h-5 opacity-40" /></div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-semibold truncate">{r.entity?.title || 'Deleted item'}</p>
                    <Badge className="text-[10px] bg-rose-500/15 text-rose-200 border border-rose-400/25">{REPORT_REASON_LABEL[r.reason] || r.reason}</Badge>
                  </div>
                  <p className="text-xs mt-1" style={{ color: 'var(--jw-text-muted)' }}>
                    By <b>{r.reporter?.name || 'Anonymous'}</b>
                    {' · '}
                    <span className="capitalize">{(r.entity_type || '').replace('_', ' ')}</span>
                    {' · '}
                    Status <span className="capitalize">{r.status}</span>
                  </p>
                  {r.details && (
                    <p className="text-[11px] mt-2 p-2 rounded border" style={{ color: 'var(--jw-text-muted)', borderColor: 'var(--jw-border)' }}>{r.details}</p>
                  )}
                  {r.notes && (
                    <p className="text-[11px] mt-2" style={{ color: 'var(--jw-text-subtle)' }}><b>Resolution:</b> {r.notes}</p>
                  )}
                  {r.status === 'open' && (
                    <div className="mt-3 flex gap-2">
                      <Button size="sm" variant="secondary" onClick={() => openReview(r, 'dismiss')} data-testid={`admin-report-${r.id}-dismiss`} className="jw-btn-gloss-ghost h-8">Dismiss</Button>
                      <Button size="sm" onClick={() => openReview(r, 'remove')} data-testid={`admin-report-${r.id}-remove`} className="h-8 jw-btn-glossy">Remove content</Button>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!target} onOpenChange={(v) => !v && setTarget(null)}>
        <DialogContent data-testid="admin-report-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>{action === 'dismiss' ? 'Dismiss report' : 'Remove reported content'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>
              {action === 'dismiss'
                ? "The report will be closed and the content will remain live."
                : "The content will be taken down, and the creator will be notified with your reason."}
            </p>
            <div>
              <label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>Notes (optional)</label>
              <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="admin-report-notes"
                placeholder={action === 'dismiss' ? 'e.g. Reviewed — no violation.' : 'e.g. Confirmed copyrighted material.'} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setTarget(null)}>Cancel</Button>
            <Button onClick={submitReview} data-testid="admin-report-submit" className="jw-btn-glossy">
              {action === 'dismiss' ? 'Dismiss' : 'Remove'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


export default ReportsSection;
